import axiosClient, { axiosWithAuth } from '@/services/axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  openCloseCommonWarningModal,
  openRemoveUnavailableModal,
} from '../reducers/cart-modal-reducer';

const REQUEST_HEADERS = {
  'Content-Type': 'application/json',
  // 'Cache-Control': 'no-cache, no-store, must-revalidate',
  // 'Pragma': 'no-cache',
  // 'Expires': '0'
};

const prepareRequestData = (product) => {
  const request = { items: [] };
  if (Array.isArray(product) && product?.length > 0) {
    request.items = product.map((item) => ({
      product_id: item?.id,
      cart_quantity: item?.cart_quantity,
      apply_installation_fee: item?.apply_installation_fee,
    }));
    return request;
  }
  request.items = [
    ...request.items,
    {
      product_id: product?.id,
      cart_quantity: product?.cart_quantity,
      apply_installation_fee: product?.apply_installation_fee,
    },
  ];
  return request;
};

// get cart items async thunk
export const fetchCartItemsAsync = createAsyncThunk(
  'carts/fetchCartItemsAsync',
  async (_, { getState, rejectWithValue }) => {
    const { cartToken } = getState().carts;
    const { accessToken } = getState().auth;

    REQUEST_HEADERS['Cart-Token'] = cartToken;

    if (accessToken) {
      REQUEST_HEADERS.Authorization = `Bearer ${accessToken}`;
    }

    try {
      const paths = {
        guest: '/api/order/guest-carts/items',
        auth: '/api/order/carts/items',
      };

      let response = null;
      const responseData = {};

      if (!accessToken && !cartToken) {
        responseData.cart_token = null;
        responseData.items = [];
        return responseData;
      }

      if (accessToken) {
        response = await axiosWithAuth(accessToken).get(paths.auth, {
          headers: REQUEST_HEADERS,
        });
      } else {
        response = await axiosClient.get(paths.guest, {
          headers: REQUEST_HEADERS,
        });
      }

      if (response?.status === 200) {
        const data = response?.data?.data;
        const responseCartToken = data?.cart_token;

        responseData.cart_token = responseCartToken;
        responseData.items = data?.items;
      }

      return responseData;
    } catch (error) {
      console.error('Error fetching cart items: ', error);
      return rejectWithValue(error?.response || error.message);
    }
  },
);

// Add To Cart Async Thunk
export const addCartItemsAsync = createAsyncThunk(
  'carts/addCartItemsAsync',
  async ({ product, type }, { getState, dispatch, rejectWithValue }) => {
    const { items: cart, cartToken } = getState().carts;
    const { accessToken } = getState().auth;

    REQUEST_HEADERS['Cart-Token'] = cartToken;
    // if (cartToken) {
    // }

    if (accessToken) {
      REQUEST_HEADERS.Authorization = `Bearer ${accessToken}`;
    }

    try {
      const paths = {
        guest: '/api/order/guest-carts/items',
        auth: '/api/order/carts/items',
      };

      let response = null;

      if (accessToken) {
        response = await axiosWithAuth(accessToken).post(
          paths.auth,
          prepareRequestData(product),
          { headers: REQUEST_HEADERS },
        );
      } else {
        response = await axiosClient.post(
          paths.guest,
          prepareRequestData(product),
          { headers: REQUEST_HEADERS },
        );
      }

      const data = await response?.data?.data;

      if (data?.message === 'update_success') {
        return {
          cart_token: data?.cart_token,
          id: product?.id,
          type,
        };
      }

      return data;
    } catch (error) {
      dispatch(
        openCloseCommonWarningModal({
          isOpen: true,
          text: error.response?.data?.error?.message,
        }),
      );
      console.error('Error adding cart items: ', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Remove item from Cart Async Thunk
export const removeCartItemsAsync = createAsyncThunk(
  'carts/removeCartItemsAsync',
  async (product, { getState, dispatch, rejectWithValue }) => {
    const { items: cart, cartToken } = getState().carts;
    const { accessToken } = getState().auth;

    REQUEST_HEADERS['Cart-Token'] = cartToken;
    // if (cartToken) {
    // }

    if (accessToken) {
      REQUEST_HEADERS.Authorization = `Bearer ${accessToken}`;
    }

    const productId = product?.id || product?.currentVariantId || 0;

    if (productId === 0) {
      return cart;
    }

    try {
      const paths = {
        guest: `/api/order/guest-carts/remove/${productId}`,
        auth: `/api/order/carts/remove/${productId}`,
      };
      let response = null;

      if (accessToken) {
        response = await axiosWithAuth(accessToken).delete(paths.auth, {
          headers: REQUEST_HEADERS,
        });
      } else {
        response = await axiosClient.delete(paths.guest, {
          headers: REQUEST_HEADERS,
        });
      }

      // dispatch(removeFromCart(productId));
      const data = response?.data;
      return productId;
    } catch (error) {
      dispatch(
        openCloseCommonWarningModal({
          isOpen: true,
          text: error.response?.data?.error?.message,
        }),
      );
      console.error('Error removing item cart: ', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Remove Cart Async Thunk
export const removeCartAsync = createAsyncThunk(
  'carts/removeCartAsync',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { items: cart, cartToken } = getState().carts;
    const { accessToken } = getState().auth;

    REQUEST_HEADERS['Cart-Token'] = cartToken;
    // if (cartToken) {
    // }

    if (accessToken) {
      REQUEST_HEADERS.Authorization = `Bearer ${accessToken}`;
    }

    try {
      const paths = {
        guest: '/api/order/guest-carts',
        auth: '/api/order/carts',
      };

      let response = null;

      if (accessToken) {
        response = await axiosWithAuth(accessToken).delete(paths.auth, {
          headers: REQUEST_HEADERS,
        });
      } else {
        response = await axiosClient.delete(paths.guest, {
          headers: REQUEST_HEADERS,
        });
      }

      const data = response?.data;
      return data;
    } catch (error) {
      dispatch(
        openCloseCommonWarningModal({
          isOpen: true,
          text: error.response?.data?.error?.message,
        }),
      );
      console.error('Error removing cart: ', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Remove Unavailable Items from Cart Async Thunk
export const removeUnavailableAsync = createAsyncThunk(
  'carts/removeUnavailableAsync',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { cartToken } = getState().carts;
    const { accessToken } = getState().auth;

    REQUEST_HEADERS['Cart-Token'] = cartToken;
    // if (cartToken) {
    // }

    if (accessToken) {
      REQUEST_HEADERS.Authorization = `Bearer ${accessToken}`;
    }

    try {
      const paths = {
        guest: '/api/order/guest-carts/unavailable',
        auth: '/api/order/carts/unavailable',
      };

      let response = null;

      if (accessToken) {
        response = await axiosWithAuth(accessToken).delete(paths.auth, {
          headers: REQUEST_HEADERS,
        });
      } else {
        response = await axiosClient.delete(paths.guest, {
          headers: REQUEST_HEADERS,
        });
      }
      dispatch(openRemoveUnavailableModal({ type: 'unavailable' }));
      const data = response?.data?.data;
      return data;
    } catch (error) {
      dispatch(
        openCloseCommonWarningModal({
          isOpen: true,
          text: error.response?.data?.error?.message,
        }),
      );
      console.error('Error removing unavailables from cart: ', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// for buy now
export const buyNowAsync = createAsyncThunk(
  'carts/buyNowAsync',
  async ({ product }, { getState, dispatch, rejectWithValue }) => {
    const { cartToken } = getState().carts;
    const { accessToken } = getState().auth;

    REQUEST_HEADERS['Cart-Token'] = null;

    if (accessToken) {
      REQUEST_HEADERS.Authorization = `Bearer ${accessToken}`;
    }

    try {
      const paths = {
        guest: '/api/order/guest-carts/items',
        auth: '/api/order/carts/items',
      };

      let response = null;

      if (accessToken) {
        response = await axiosWithAuth(accessToken).post(
          paths.auth,
          prepareRequestData(product),
          { headers: REQUEST_HEADERS },
        );
      } else {
        response = await axiosClient.post(
          paths.guest,
          prepareRequestData(product),
          { headers: REQUEST_HEADERS },
        );
      }

      let data = await response?.data?.data;

      if (!accessToken) {
        data = { ...data, prevToken: cartToken };
      }

      return data;
    } catch (error) {
      dispatch(
        openCloseCommonWarningModal({
          isOpen: true,
          text: error.response?.data?.error?.message,
        }),
      );
      console.error('Error buy now item: ', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
